import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Edit3, X, LogOut, ImageIcon, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  adminVerify, adminListProperties, adminCreateProperty,
  adminUpdateProperty, adminDeleteProperty,
  adminListEnquiries, adminListAppointments, formatZAR,
} from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ImageUploader } from "@/components/ImageUploader";
import { BusinessAssets } from "@/components/BusinessAssets";

const STORAGE_KEY = "campusco_admin_creds";

const loadCreds = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { email: "", password: "" };
  } catch { return { email: "", password: "" }; }
};

const EMPTY_FORM = {
  listing_type: "sales",
  title: "",
  city: "",
  address: "",
  price: "",
  bedrooms: "",
  bathrooms: "",
  size_sqm: "",
  description: "",
  features: "",
  images: "",
  nearby_institution: "",
  available_from: "",
  nsfas_accredited: false,
  featured: false,
};

const toPayload = (f) => ({
  listing_type: f.listing_type,
  title: f.title.trim(),
  city: f.city.trim(),
  address: f.address.trim(),
  price: Number(f.price),
  bedrooms: Number(f.bedrooms || 0),
  bathrooms: Number(f.bathrooms || 0),
  size_sqm: f.size_sqm ? Number(f.size_sqm) : null,
  description: f.description.trim(),
  features: f.features.split(",").map((s) => s.trim()).filter(Boolean),
  images: f.images.split("\n").map((s) => s.trim()).filter(Boolean),
  nearby_institution: f.nearby_institution.trim() || null,
  available_from: f.available_from.trim() || null,
  nsfas_accredited: !!f.nsfas_accredited,
  featured: !!f.featured,
});

export default function Admin() {
  const [creds, setCreds] = useState(loadCreds);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [properties, setProperties] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (creds.email && creds.password) tryLogin(creds, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryLogin = async (c, silent = false) => {
    setChecking(true);
    try {
      await adminVerify(c);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
      setAuthed(true);
      loadAll(c);
    } catch {
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
      if (!silent) toast.error("Invalid email or password");
    } finally {
      setChecking(false);
    }
  };

  const loadAll = async (c) => {
    try {
      const [props, enqs, appts] = await Promise.all([
        adminListProperties(),
        adminListEnquiries(c),
        adminListAppointments(c),
      ]);
      setProperties(props);
      setEnquiries(enqs);
      setAppointments(appts);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCreds({ email: "", password: "" });
    setAuthed(false);
    setProperties([]);
    setEnquiries([]);
    setAppointments([]);
  };

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const editProperty = (p) => {
    setEditingId(p.id);
    setForm({
      listing_type: p.listing_type,
      title: p.title || "",
      city: p.city || "",
      address: p.address || "",
      price: p.price ?? "",
      bedrooms: p.bedrooms ?? "",
      bathrooms: p.bathrooms ?? "",
      size_sqm: p.size_sqm ?? "",
      description: p.description || "",
      features: (p.features || []).join(", "),
      images: (p.images || []).join("\n"),
      nearby_institution: p.nearby_institution || "",
      available_from: p.available_from || "",
      nsfas_accredited: !!p.nsfas_accredited,
      featured: !!p.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitProperty = async (e) => {
    e.preventDefault();
    if (!form.title || !form.city || !form.price || !form.description) {
      toast.error("Please fill title, city, price and description.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await adminUpdateProperty(creds, editingId, toPayload(form));
        toast.success("Property updated");
      } else {
        await adminCreateProperty(creds, toPayload(form));
        toast.success("Property added");
      }
      resetForm();
      loadAll(creds);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeProperty = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await adminDeleteProperty(creds, id);
      toast.success("Deleted");
      loadAll(creds);
    } catch {
      toast.error("Delete failed");
    }
  };

  const imagePreviewUrls = useMemo(
    () => form.images.split("\n").map((s) => s.trim()).filter(Boolean),
    [form.images]
  );

  // ---------- Login gate ----------
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo variant="primary" className="h-12" />
          </div>
          <div className="bg-background border border-border p-8">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-secondary" />
              <h1 className="font-serif text-2xl">Admin Access</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your admin email and password to manage properties, enquiries and appointments.
            </p>
            <form
              className="mt-6 space-y-4"
              onSubmit={(e) => { e.preventDefault(); tryLogin(creds); }}
            >
              <div>
                <Label htmlFor="ad-email">Email</Label>
                <Input
                  id="ad-email" type="email" value={creds.email}
                  onChange={(e) => setCreds({ ...creds, email: e.target.value })}
                  className="mt-1.5 h-11 rounded-sm" autoFocus
                  data-testid="admin-email" autoComplete="username"
                  placeholder="admin@thecampusco.co.za"
                />
              </div>
              <div>
                <Label htmlFor="ad-pw">Password</Label>
                <Input
                  id="ad-pw" type="password" value={creds.password}
                  onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                  className="mt-1.5 h-11 rounded-sm"
                  data-testid="admin-password" autoComplete="current-password"
                />
              </div>
              <Button type="submit" disabled={checking} className="w-full h-11 rounded-sm bg-primary text-primary-foreground" data-testid="admin-login-btn">
                {checking ? "Checking…" : "Sign in"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Authenticated ----------
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
      {/* Admin header with logo */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-8 border-b border-border">
        <Link to="/" data-testid="admin-logo-link">
          <Logo variant="primary" className="h-12" />
        </Link>
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" className="rounded-sm">
            <Link to="/" target="_blank" rel="noopener" data-testid="admin-view-site">
              <Eye className="w-4 h-4 mr-2" /> View live site
            </Link>
          </Button>
          <Button variant="outline" onClick={logout} className="rounded-sm" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </div>

      <div className="mt-8">
        <p className="overline text-secondary">Campusco Admin</p>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Manage properties &amp; enquiries</h1>
        <p className="mt-2 text-sm text-muted-foreground">Private area — this URL is not linked from the public site. Bookmark <code className="text-primary">/admin</code> for future visits.</p>
      </div>

      <Tabs defaultValue="properties" className="mt-10">
        <TabsList className="rounded-sm">
          <TabsTrigger value="properties" data-testid="admin-tab-properties">Properties ({properties.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="admin-tab-appointments">Appointments ({appointments.length})</TabsTrigger>
          <TabsTrigger value="assets" data-testid="admin-tab-assets">Assets &amp; Downloads</TabsTrigger>
        </TabsList>

        {/* Properties */}
        <TabsContent value="properties" className="mt-8">
          <form onSubmit={submitProperty} className="bg-background border border-border p-6 md:p-8 space-y-6" data-testid="admin-property-form">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-serif text-2xl">{editingId ? "Edit Property" : "Add New Property"}</h2>
              {editingId && (
                <Button type="button" variant="ghost" onClick={resetForm} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-1" /> Cancel edit
                </Button>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label>Listing type</Label>
                <Select value={form.listing_type} onValueChange={(v) => setForm({ ...form, listing_type: v })}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">For Sale</SelectItem>
                    <SelectItem value="leasing">For Lease</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-title" placeholder="e.g. Rosebank Student Tower" />
              </div>
              <div>
                <Label>City *</Label>
                <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-city" placeholder="Johannesburg" />
              </div>
              <div>
                <Label>Address *</Label>
                <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-address" placeholder="17 Cradock Avenue, Rosebank" />
              </div>
              <div>
                <Label>Price (ZAR) *</Label>
                <Input required type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-price" placeholder={form.listing_type === "leasing" ? "e.g. 4500 (per month)" : "e.g. 78500000"} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Bedrooms</Label>
                  <Input type="number" min="0" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-bedrooms" />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input type="number" min="0" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-bathrooms" />
                </div>
                <div>
                  <Label>Size (m²)</Label>
                  <Input type="number" min="0" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-size" />
                </div>
              </div>
              <div>
                <Label>Nearby institution</Label>
                <Input value={form.nearby_institution} onChange={(e) => setForm({ ...form, nearby_institution: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-institution" placeholder="e.g. University of the Witwatersrand" />
              </div>
              <div>
                <Label>Available from</Label>
                <Input value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-available" placeholder="Immediate / 2026-02-01" />
              </div>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-sm" data-testid="admin-form-description" placeholder="Overview of the property, its features, and target market." />
            </div>

            <div>
              <Label>Features (comma separated)</Label>
              <Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} className="mt-1.5 h-11 rounded-sm" data-testid="admin-form-features" placeholder="Fibre WiFi, 24/7 Security, Backup Power, Gym" />
            </div>

            <div>
              <Label className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Property images</Label>
              <div className="mt-2">
                <ImageUploader
                  creds={creds}
                  onUrls={(newUrls) => {
                    const current = form.images ? form.images.split("\n").filter(Boolean) : [];
                    setForm({ ...form, images: [...current, ...newUrls].join("\n") });
                  }}
                />
              </div>

              <details className="mt-3">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none" data-testid="admin-toggle-manual-urls">
                  Or paste image URLs manually
                </summary>
                <Textarea rows={3} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="mt-2 rounded-sm font-mono text-xs" data-testid="admin-form-images" placeholder="https://images.unsplash.com/…&#10;https://images.pexels.com/…" />
              </details>

              {imagePreviewUrls.length > 0 && (
                <div className="mt-4">
                  <p className="overline text-secondary mb-3">Image previews ({imagePreviewUrls.length})</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="admin-image-previews">
                    {imagePreviewUrls.map((url, i) => (
                      <div key={`${url}-${i}`} className="relative aspect-[4/3] bg-muted border border-border overflow-hidden group">
                        <img
                          src={url}
                          alt={`Preview ${i + 1}`}
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-1 left-1 bg-background/90 backdrop-blur px-1.5 py-0.5 text-[10px] font-semibold">{i === 0 ? "COVER" : `#${i + 1}`}</div>
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            const next = imagePreviewUrls.filter((_, j) => j !== i);
                            setForm({ ...form, images: next.join("\n") });
                          }}
                          className="absolute top-1 right-1 bg-background/90 backdrop-blur w-6 h-6 rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove image"
                          data-testid={`admin-image-remove-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} data-testid="admin-form-featured" />
                Featured on home page
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.nsfas_accredited} onCheckedChange={(v) => setForm({ ...form, nsfas_accredited: !!v })} data-testid="admin-form-nsfas" />
                NSFAS accredited
              </label>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={saving} className="rounded-sm h-11 px-6 bg-primary text-primary-foreground" data-testid="admin-form-submit">
                {saving ? "Saving…" : editingId ? "Update Property" : (<><Plus className="w-4 h-4 mr-1.5" /> Add Property</>)}
              </Button>
            </div>
          </form>

          <div className="mt-10">
            <h3 className="font-serif text-xl mb-4">Existing properties</h3>
            <div className="border border-border divide-y divide-border">
              {properties.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 hover:bg-muted/30">
                  <img
                    src={p.images?.[0] || "https://via.placeholder.com/120x80"}
                    alt=""
                    className="w-24 h-16 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.city} · {p.listing_type === "leasing" ? "Lease" : "Sale"} · {formatZAR(p.price)}
                      {p.featured && <span className="ml-2 overline text-[9px] text-secondary">FEATURED</span>}
                      {p.nsfas_accredited && <span className="ml-2 overline text-[9px] text-secondary">NSFAS</span>}
                    </p>
                  </div>
                  <Button asChild variant="ghost" size="sm" data-testid={`admin-preview-${p.id}`}>
                    <Link to={`/property/${p.id}`} target="_blank" rel="noopener">
                      <Eye className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => editProperty(p)} data-testid={`admin-edit-${p.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => removeProperty(p.id)} data-testid={`admin-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
              {properties.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No properties yet.</div>}
            </div>
          </div>
        </TabsContent>

        {/* Enquiries */}
        <TabsContent value="enquiries" className="mt-8">
          <div className="border border-border divide-y divide-border">
            {enquiries.map((e) => (
              <div key={e.id} className="p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg">{e.name} <span className="overline text-[10px] text-secondary ml-2">{e.enquiry_type}</span></p>
                    <p className="text-xs text-muted-foreground">{e.email} · {e.phone}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{e.message}</p>
              </div>
            ))}
            {enquiries.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No enquiries yet.</div>}
          </div>
        </TabsContent>

        {/* Appointments */}
        <TabsContent value="appointments" className="mt-8">
          <div className="border border-border divide-y divide-border">
            {appointments.map((a) => (
              <div key={a.id} className="p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg">{a.name} <span className="overline text-[10px] text-secondary ml-2">{a.topic}</span></p>
                    <p className="text-xs text-muted-foreground">{a.email} · {a.phone} {a.company ? `· ${a.company}` : ""}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {a.date} @ {a.time_slot}
                  </p>
                </div>
                {a.notes && <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{a.notes}</p>}
              </div>
            ))}
            {appointments.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No appointments yet.</div>}
          </div>
        </TabsContent>

        {/* Assets & Downloads */}
        <TabsContent value="assets" className="mt-8">
          <div className="mb-6">
            <h2 className="font-serif text-2xl">Business assets &amp; downloads</h2>
            <p className="mt-1 text-sm text-muted-foreground">Internal use only — logo pack, letterhead and the services brochure. Not linked from the public site.</p>
          </div>
          <BusinessAssets />
        </TabsContent>
      </Tabs>
    </div>
  );
}
